import { Component } from '@theme/component';

/**
 * Arrow controls for carousels built on native scroll snapping.
 *
 * The theme's slideshow-component owns its slides and animates between them.
 * These carousels are plain scroll containers instead, so this only needs to
 * nudge the scroll position and keep the arrows in sync with it.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} viewport - The scrolling element
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class ScrollCarousel extends Component {
  requiredRefs = ['viewport'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.viewport.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);

    // Slide widths depend on layout, so wait for the first paint
    requestAnimationFrame(() => this.#sync());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.viewport.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  /** Called by the next arrow via on:click="/next" */
  next() {
    this.#scrollByStep(1);
  }

  /** Called by the previous arrow via on:click="/previous" */
  previous() {
    this.#scrollByStep(-1);
  }

  /** @param {number} direction 1 forwards, -1 backwards */
  #scrollByStep(direction) {
    this.refs.viewport.scrollBy({ left: this.#step() * direction, behavior: 'smooth' });
  }

  /**
   * One slide plus the gap, measured from the first two slides so it stays
   * correct whatever the cards-per-view setting is.
   *
   * @returns {number}
   */
  #step() {
    const { viewport } = this.refs;
    const slides = viewport.querySelectorAll('[data-carousel-slide]');

    if (slides.length > 1) {
      const first = slides[0].getBoundingClientRect();
      const second = slides[1].getBoundingClientRect();
      const stride = Math.round(Math.abs(second.left - first.left));

      if (stride > 0) return stride;
    }

    if (slides.length === 1) return Math.round(slides[0].getBoundingClientRect().width);

    return viewport.clientWidth;
  }

  #onScroll = () => this.#sync();

  /** Disables an arrow at each end, and hides both when nothing overflows. */
  #sync() {
    const { viewport, previous, next } = this.refs;
    const overflow = viewport.scrollWidth - viewport.clientWidth;

    // scrollLeft is negative in right-to-left layouts
    const position = Math.abs(viewport.scrollLeft);

    this.toggleAttribute('data-no-overflow', overflow <= 1);

    previous?.toggleAttribute('disabled', position <= 1);
    next?.toggleAttribute('disabled', position >= overflow - 1);
  }
}

if (!customElements.get('scroll-carousel-component')) {
  customElements.define('scroll-carousel-component', ScrollCarousel);
}
